import { Container, Sprite, type Text } from 'pixi.js';
import { RUN_NAME, friendAlias, type FriendInfo, type RoadLamp } from '@goodtime/shared';
import { glowTexture, prefersReducedMotion } from './glow';
import { artSprite, displayText, lampFace, uiText, type ArtLookup } from './friendPieces';

const PORTRAIT = 150;
const LAMP = 46;

/**
 * What a hello card shows: a picture (a friend's portrait, or a lamp drawn
 * large), the words under it, and optionally a lamp with its game's name.
 */
export interface Greeting {
  /** A friend's portrait by alias, or a lamp to draw in its place. */
  portrait: { alias: string } | { lamp: RoadLamp };
  /** The words under the picture, as they're shown. */
  line: string;
  /** The lamp now on the run, with its game's name; none for a notice. */
  lamp?: { face: RoadLamp; title: string; sub: string };
}

/** A friend's hello: their portrait, their words, and their game on its lamp. */
export function friendGreeting(friend: FriendInfo): Greeting {
  return {
    portrait: { alias: friendAlias(friend.id, 'greet') },
    line: `“${friend.line}”`,
    lamp: { face: { id: 'lamp1', lit: true, friend: friend.id, game: friend.game }, title: friend.title, sub: `On the ${RUN_NAME} from now on` },
  };
}

/**
 * Inside the dialog when a friend joins (their lamp has just lit): the
 * friend saying hello, their words, and their game now on its lamp. On a
 * Wheel Run slot, where a star lights a lamp, the lamp itself stands in for
 * the friend. The dialog around it carries the title ("HOOT JOINS YOU") and
 * the buttons: LOVELY, or Later and PLAY FREE while the first game is free.
 */
export class FriendJoinContent extends Container {
  private readonly glow = new Sprite(glowTexture());
  private readonly portrait: Container | null;
  private readonly line: Text;
  private readonly lamp: Container | null;
  private readonly title: Text | null;
  private readonly sub: Text | null;
  private readonly free: Text | null;

  constructor(greeting: Greeting | FriendInfo, art: ArtLookup, freePlay: boolean) {
    super();
    const g = 'portrait' in greeting ? greeting : friendGreeting(greeting);
    this.glow.anchor.set(0.5);
    this.glow.tint = 0xffd66e;
    this.glow.alpha = 0.6;
    this.portrait =
      'alias' in g.portrait ? artSprite(art, g.portrait.alias, PORTRAIT) : lampFace(g.portrait.lamp, Math.round(PORTRAIT * 0.8), art, false);
    this.line = uiText(g.line, 16, 300);
    this.lamp = g.lamp ? lampFace(g.lamp.face, LAMP, art) : null;
    this.title = g.lamp ? displayText(g.lamp.title, 19) : null;
    this.title?.anchor.set(0, 0);
    this.sub = g.lamp ? uiText(g.lamp.sub, 14, 220, { align: 'left', fontWeight: '500' }) : null;
    if (this.sub) {
      this.sub.anchor.set(0, 0);
      this.sub.alpha = 0.85;
    }
    this.free = freePlay ? uiText('Your first game is free.', 15, 300) : null;
    this.addChild(this.glow, ...(this.portrait ? [this.portrait] : []), this.line);
    if (this.lamp && this.title && this.sub) this.addChild(this.lamp, this.title, this.sub);
    if (this.free) this.addChild(this.free);
  }

  /** Lay out `width` across from y = 0; returns the height used. */
  layout(width: number): number {
    const cx = width / 2;
    let y = 0;
    this.glow.width = this.glow.height = PORTRAIT * 1.2;
    this.glow.position.set(cx, y + PORTRAIT / 2);
    if (this.portrait) this.portrait.position.set(cx, y + PORTRAIT / 2);
    y += PORTRAIT + 4;
    this.line.style.wordWrapWidth = width - 8;
    this.line.position.set(cx, y);
    y += this.line.height + 14;

    // The lamp and the game's name, centred as a pair.
    if (this.lamp && this.title && this.sub) {
      const rowH = 60;
      this.sub.style.wordWrapWidth = Math.min(220, width - LAMP - 24);
      const pair = LAMP + 12 + Math.max(this.title.width, this.sub.width);
      const left = Math.max(0, (width - pair) / 2);
      this.lamp.position.set(left + LAMP / 2, y + rowH / 2);
      const textX = left + LAMP + 12;
      const block = this.title.height + 2 + this.sub.height;
      this.title.position.set(textX, y + (rowH - block) / 2);
      this.sub.position.set(textX, this.title.y + this.title.height + 2);
      y += rowH;
    } else {
      y -= 8;
    }
    if (this.free) {
      this.free.position.set(cx, y + 12);
      y += 12 + this.free.height;
    }
    return y;
  }

  /** The picture bounces up into place (still, with reduced motion). */
  enter(): void {
    const who = this.portrait;
    if (!who || prefersReducedMotion()) return;
    const start = performance.now();
    const base = who.scale.x;
    const y0 = who.y;
    const step = (now: number) => {
      if (who.destroyed) return;
      const t = Math.min(1, (now - start) / 650);
      // Overshoot ease: up from below, a little past, then settle.
      const e = 1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2);
      who.alpha = Math.min(1, t * 2.5);
      who.scale.set(base * (0.6 + 0.4 * e));
      who.y = y0 + 40 * (1 - e);
      if (t < 1) requestAnimationFrame(step);
      else {
        who.scale.set(base);
        who.y = y0;
      }
    };
    requestAnimationFrame(step);
  }
}
